import type { ComponentType } from "react";
import { DEV_MERCHANT_JOB_TYPE } from "@cpos/shared/src/dev-merchant/spec";
import { SimulateStripeIdentityEventSheet } from "./components/SimulateStripeIdentityEventSheet";
import { BackfillIdentityDedupeJobSheet } from "./components/BackfillIdentityDedupeJobSheet";
import { ForceVerifiedIdentitySheet } from "./components/ForceVerifiedIdentitySheet";
import { RepairDatabaseJobSheet } from "./components/RepairDatabaseJobSheet";
import { ActivationBonusBackfillSheet } from "./components/ActivationBonusBackfillSheet";
import { EntryModeBackfillSheet } from "./components/EntryModeBackfillSheet";
import { ProductUpdateBroadcastSheet } from "./components/ProductUpdateBroadcastSheet";
import { PoyntSaleBackfillSheet } from "./components/PoyntSaleBackfillSheet";

export interface ToolSheetProps {
  onClose: () => void;
}

interface ToolBase {
  id: string;
  title: string;
  description: string;
  /**
   * Job type this tool enqueues, for tools that run as background jobs.
   *
   * Present means the tool has a run history to link to; absent means it acts
   * synchronously and leaves no Job rows behind.
   */
  jobType?: string;
}

export type ToolDefinition =
  | (ToolBase & {
      kind: "oneClick";
      run: () => Promise<unknown>;
    })
  | (ToolBase & {
      kind: "custom";
      Sheet: ComponentType<ToolSheetProps>;
    })
  | (ToolBase & {
      kind: "route";
      path: string;
    });

export const TOOLS: ToolDefinition[] = [
  {
    kind: "custom",
    id: "repair-database",
    jobType: "reconcile",
    title: "Repair Database",
    description:
      "Re-derives every merchant's lifecycle state and billing projection from the durable record rather than cached flags. Chains across as many jobs as the sweep needs; also runs nightly at 03:00 UTC.",
    Sheet: RepairDatabaseJobSheet,
  },
  {
    kind: "custom",
    id: "activation-bonus-backfill",
    jobType: "activation-bonus-backfill",
    title: "Activation Bonus Backfill",
    description:
      "Pays the $5 activation bonus to merchants supplied by email. Resolves each address against the lifecycle record and Stripe first, shows exactly who will be paid and the total spend, and only then transfers. Merchants already paid — including by hand in the Stripe dashboard — are skipped.",
    Sheet: ActivationBonusBackfillSheet,
  },
  {
    kind: "custom",
    id: "poynt-sale-backfill",
    jobType: "poynt-sale-backfill",
    title: "Poynt Sale Backfill",
    description:
      "Finds Poynt sales missing from CPOS for one merchant and a window of up to 62 days. Preview reads Poynt and writes nothing; the write run creates only the sales the preview listed.",
    Sheet: PoyntSaleBackfillSheet,
  },
  {
    kind: "custom",
    id: "entry-mode-backfill",
    jobType: "reconcile",
    title: "Entry Mode Backfill",
    description:
      "Recovers the entry mode of settled charges that never recorded one, for a pasted list of merchants. A tap that went unrecorded holds a merchant on the first-sale setup step forever. Audit first — it reports what the processor can still resolve without writing.",
    Sheet: EntryModeBackfillSheet,
  },
  {
    kind: "custom",
    id: "product-update-broadcast",
    jobType: "product-update-broadcast",
    title: "Product Update Broadcast",
    description:
      "Sends one authored update to every merchant as an informational notification. Merchants who switched product updates off receive nothing. Re-sending the same broadcast notifies nobody twice.",
    Sheet: ProductUpdateBroadcastSheet,
  },
  {
    kind: "custom",
    id: "identity-backfill",
    jobType: "identity-backfill-dedupe",
    title: "Identity Dedupe Backfill",
    description:
      "Processes IDV-verified merchants without a captured identity hash. Kicks off an async job and streams live progress. Cancel at any checkpoint.",
    Sheet: BackfillIdentityDedupeJobSheet,
  },
  {
    kind: "custom",
    id: "force-verified-identity",
    title: "Force Verified Identity",
    description:
      "Drive the real verified-identity path from supplied PII, with a stub Stripe client. Sandbox + preprod only.",
    Sheet: ForceVerifiedIdentitySheet,
  },
  {
    kind: "custom",
    id: "simulate-stripe-identity-event",
    title: "Simulate Stripe Identity Event",
    description:
      "Inject a synthetic Identity event so the stream runs the normal processor chain. Sandbox + preprod only.",
    Sheet: SimulateStripeIdentityEventSheet,
  },
  {
    kind: "route",
    id: "create-merchant",
    jobType: DEV_MERCHANT_JOB_TYPE,
    title: "Create Merchant (dev)",
    description:
      "Manufacture a fully functional test merchant — Cognito login, business profile, Stripe test-mode account, transactions, lifecycle. Staging/sandbox only.",
    path: "/tools/create-merchant",
  },
];
